import { type User, type UserAttributes } from '@kalos/kalos-rpc';
import {
  Avatar,
  AvatarFallback,
  AvatarImage,
  Button,
  Checkbox,
  DateInput,
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
  MultipleSelect,
  PhoneInput,
  SimpleSelect,
} from '@kalos/ui';
import { useQuery } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { type SubmitHandler, useWatch } from 'react-hook-form';
import { type Country } from 'react-phone-number-input';

import {
  MONTHS,
  SHIRT_SIZES,
  USA_STATES_OPTIONS,
  WORKERS_COMP_CODES,
} from '../../../../../constants';
import { useAuth } from '../../../../../context/AuthContext';
import { getEmployeeImageQueryConfig } from '../../../../../hooks/react-query/useEmployeesQuery';
import { useEventTradesQuery } from '../../../../../hooks/react-query/useEventTradesQuery';
import { useJobTypesQuery } from '../../../../../hooks/react-query/useJobTypesQuery';
import { useApplyTeamChanges } from '../../../../../hooks/react-query/useOrgStructureQuery';
import { resolveEmployeeTradeCode } from '../../../../../tools/employeeTradeCode';
import { type DirtyModelFields } from '../../../../../tools/helpers';
import { orgChartAdminCheck } from '../../../../../tools/permissionChecks';
import { includesTypeGuard } from '../../../../../tools/typeguargs';
import { isTradeId, tradeLabel } from '../../../../../utils/eventTrade';
import { JobTitlePicker } from '../../../OrgStructure/JobTitlePicker';
import { TeamMultiSelect } from '../../../OrgStructure/TeamMultiSelect';
import { TechniciansComboboxPicker } from '../../../Pickers/newPickers/TechnicianComboboxPicker';
import {
  EMPLOYEE_LANGUAGE_NONE,
  EMPLOYEE_LANGUAGE_VALUES,
  EMPLOYEE_LANGUAGES,
  EMPLOYEE_SECTORS,
  type EmployeeFormType,
  getEmployeeFormDefaults,
  useEmployeeForm,
} from './EmployeeForm.hooks';

const allowedCountries: Country[] = ['US'];

interface EmployeeFormProps {
  isAdmin: boolean;
  isHR: boolean;
  user: User;
  userAttributes: UserAttributes;
  limitedAccess?: boolean;
  pendingEmployeeEditing?: { id: number } | undefined;
  departmentsQuery: {
    data?: { results: { id: number; value: string; description: string; tradeName: string }[] };
  };
  associatedPto?: { totalHours: number } | null;
  saving: boolean;
  disableProfileImageField?: boolean;
  onSave: (
    data: EmployeeFormType,
    dirtyFields: DirtyModelFields<EmployeeFormType>,
    hireDate: Date,
    file: string | undefined,
  ) => void;
}

const monthsSelectOptions = [
  { label: 'No override', value: '0' },
  ...MONTHS.map((month, index) => ({
    label: month,
    value: (index + 1).toString(),
  })),
];

export const EmployeeForm: React.FC<EmployeeFormProps> = ({
  isAdmin,
  disableProfileImageField,
  user,
  userAttributes,
  limitedAccess,
  pendingEmployeeEditing,
  departmentsQuery,
  associatedPto,
  isHR,
  saving,
  onSave,
}) => {
  const loggedInUserId = useAuth().user.id;
  // Privilege 116, separate from the HR gate the rest of this form uses: the
  // title and team here write the org chart's structure, which its admins own.
  /*
    Behind org-chart.module along with the rest of the feature. The permission
    and the flag are read separately because a hook cannot be called
    conditionally — and because the two mean different things: one is who may
    use this, the other is whether it exists yet.
  */
  /*
    The title picker and the team field go by the permission alone. With the
    flag off their admin fell through to the free-text title, which writes
    emp_title and leaves role_id on the title the person held before — and the
    directory reads the role first, so the old title kept showing. Nobody
    without the permission sees either control, so the flag had nothing to hide
    on this form.
  */
  const hasOrgChartPermission = orgChartAdminCheck(useAuth().user);
  const defaultValues = useMemo(() => {
    const shirtSize = userAttributes.shirtSize;
    return getEmployeeFormDefaults({
      defaultValues: {
        ...user,
        hireDate: user.hireDate ? new Date(user.hireDate) : new Date(),
        overrideMonth: userAttributes.overrideMonth,
        pantSize: userAttributes.pantSize,
        shirtSize: includesTypeGuard(SHIRT_SIZES, shirtSize) ? shirtSize : undefined,
        userSector: user.userSector || 0,
        // Default Trade to the department-derived code when unset so the picker shows the
        // employee's department instead of blank (matches the dispatchable_techs view fallback).
        employeeTradeId: resolveEmployeeTradeCode(user),
        // The proto types this as a plain string; anything unrecognised reads as blank.
        employeeLanguage: includesTypeGuard(EMPLOYEE_LANGUAGE_VALUES, user.employeeLanguage)
          ? user.employeeLanguage
          : '',
      },
    });
  }, [user, userAttributes.overrideMonth, userAttributes.pantSize, userAttributes.shirtSize]);
  const form = useEmployeeForm({ defaultValues: defaultValues, employee: user });
  const imageWatch = useWatch({ control: form.control, name: 'image' });
  const employeeTradeIdWatch = useWatch({ control: form.control, name: 'employeeTradeId' });
  const isHvacTechWatch = useWatch({ control: form.control, name: 'isHvacTech' });
  const { data: tradeData } = useEventTradesQuery();
  const departmentList = departmentsQuery.data?.results ?? [];
  // Keep a legacy non-trade code visible so the picker never reads blank
  const tradeOptionIds = [
    ...(tradeData ?? []).map((t) => t.id),
    ...(employeeTradeIdWatch && !isTradeId(employeeTradeIdWatch) ? [employeeTradeIdWatch] : []),
  ];
  const tradeOptions = tradeOptionIds.map((id) => ({
    label: tradeLabel(id, tradeData ?? [], departmentList),
    value: id.toString(),
  }));

  const [employeeUploadedPhoto, setEmployeeUploadedPhoto] = useState<string | undefined>();

  // Teams live in group_member rather than on the user, so they cannot be a
  // form field — but they must still make Save live and still be discarded by
  // Cancel, so the selection is staged here and applied on submit.
  const { stored: storedTeamIds, apply: applyTeamChanges } = useApplyTeamChanges(user.id);
  const [teamIds, setTeamIds] = useState<number[]>(storedTeamIds);
  useEffect(() => setTeamIds(storedTeamIds), [storedTeamIds]);
  const teamsDirty =
    teamIds.length !== storedTeamIds.length ||
    [...teamIds].sort((a, b) => a - b).some((id, i) => id !== storedTeamIds[i]);

  const handleEmployeePhotoUpload = useCallback(
    (file: File) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        const result = reader.result as string;
        setEmployeeUploadedPhoto(result);
        form.setValue('image', file.name, {
          shouldValidate: true,
          shouldDirty: true,
          shouldTouch: true,
        });
      };
      reader.readAsDataURL(file);
    },
    [form],
  );

  const handleDeleteEmployeePhoto = useCallback(() => {
    setEmployeeUploadedPhoto(undefined);
    form.setValue('image', '', {
      shouldValidate: true,
      shouldDirty: true,
      shouldTouch: true,
    });
  }, [form]);
  const imageQuery = useQuery({
    ...getEmployeeImageQueryConfig({ imageUrl: user?.image || '' }),
    enabled: !!pendingEmployeeEditing,
  });
  const onSubmitForm: SubmitHandler<EmployeeFormType> = useCallback(
    (data) => {
      const imageToSave = data.image === '' ? undefined : employeeUploadedPhoto;
      if (teamsDirty) void applyTeamChanges(teamIds);
      onSave(data, form.formState.dirtyFields, data.hireDate, imageToSave);
    },
    [
      form.formState.dirtyFields,
      onSave,
      employeeUploadedPhoto,
      teamsDirty,
      teamIds,
      applyTeamChanges,
    ],
  );

  // The form is only half the record now: a team change is a real edit even
  // when no user field moved.
  const hasChanges = form.formState.isDirty || teamsDirty;

  const jobTypesQuery = useJobTypesQuery({
    select: useCallback((data: Array<{ id: number; name?: string }>) => {
      return data.map((item) => ({
        value: item.id.toString(),
        label: item.name || '',
      }));
    }, []),
  });

  return (
    <Form {...form}>
      <form className="space-y-4" onSubmit={form.handleSubmit(onSubmitForm)}>
        <div className="flex items-center justify-between border-b pb-4">
          <div className="flex items-center gap-2">
            {!hasChanges && (
              <div className="flex items-center gap-2 text-sm text-gray-500">
                <span className="inline-block h-2 w-2 rounded-full bg-gray-400"></span>
                Make changes to enable save
              </div>
            )}
            {hasChanges && (
              <div className="flex items-center gap-2 text-sm text-green-600">
                <span className="inline-block h-2 w-2 rounded-full bg-green-500"></span>
                Changes detected - ready to save
              </div>
            )}
          </div>
          <Button type="submit" disabled={saving || !hasChanges} isLoading={saving}>
            Save
          </Button>
        </div>
        <div>
          <h3 className="text-2xl font-semibold tracking-wide">Employee Information</h3>
          <div className="grid grid-cols-1 gap-6 rounded-lg border bg-white p-6 shadow sm:grid-cols-2 md:grid-cols-3">
            <FormField
              control={form.control}
              name="firstname"
              render={({ field }) => (
                <FormItem>
                  <div className="flex flex-wrap items-center gap-2.5">
                    <FormLabel className="block text-sm font-medium text-gray-700">
                      First Name
                    </FormLabel>
                    <FormMessage />
                  </div>
                  <FormControl>
                    <Input
                      {...field}
                      className="mt-1 block w-full"
                      disabled={!isAdmin && !limitedAccess && !isHR}
                    />
                  </FormControl>
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="lastname"
              render={({ field }) => (
                <FormItem>
                  <div className="flex flex-wrap items-center gap-2.5">
                    <FormLabel className="block text-sm font-medium text-gray-700">
                      Last Name
                    </FormLabel>
                    <FormMessage />
                  </div>
                  <FormControl>
                    <Input
                      {...field}
                      className="mt-1 block w-full"
                      disabled={!isAdmin && !limitedAccess && !isHR}
                    />
                  </FormControl>
                </FormItem>
              )}
            />

            <div className="col-span-full">
              <div className="grid grid-cols-3 gap-4 md:grid-cols-6">
                <FormField
                  control={form.control}
                  name="address"
                  render={({ field }) => (
                    <FormItem className="col-span-3">
                      <div className="flex flex-wrap items-center gap-2.5">
                        <FormLabel className="block text-sm font-medium text-gray-700">
                          Street Address
                        </FormLabel>
                        <FormMessage />
                      </div>
                      <FormControl>
                        <Input {...field} disabled={!isAdmin && !limitedAccess && !isHR} />
                      </FormControl>
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="city"
                  render={({ field }) => (
                    <FormItem className="col-span-1">
                      <div className="flex flex-wrap items-center gap-2.5">
                        <FormLabel className="block text-sm font-medium text-gray-700">
                          City
                        </FormLabel>
                        <FormMessage />
                      </div>
                      <FormControl>
                        <Input
                          {...field}
                          className="mt-1 block w-full"
                          disabled={!isAdmin && !limitedAccess && !isHR}
                        />
                      </FormControl>
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="state"
                  render={({ field }) => (
                    <FormItem className="col-span-1">
                      <FormLabel className="block text-sm font-medium text-gray-700">
                        State
                      </FormLabel>
                      <SimpleSelect
                        placeholder="State"
                        onChange={(value) => field.onChange(value)}
                        disabled={!isAdmin && !limitedAccess && !isHR}
                        selectedValue={(field.value ?? '').toString()}
                        values={USA_STATES_OPTIONS.map((option) => ({
                          label: option.toString(),
                          value: option.toString(),
                        }))}
                      />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="zip"
                  render={({ field }) => (
                    <FormItem className="col-span-1">
                      <div className="flex flex-wrap items-center gap-2.5">
                        <FormLabel className="block text-sm font-medium text-gray-700">
                          Zip
                        </FormLabel>
                        <FormMessage />
                      </div>
                      <FormControl>
                        <Input
                          {...field}
                          className="mt-1 block w-full"
                          disabled={
                            !isAdmin && loggedInUserId !== user.id && limitedAccess == undefined
                          }
                        />
                      </FormControl>
                    </FormItem>
                  )}
                />
              </div>
            </div>
            <FormField
              control={form.control}
              name="phone"
              render={({ field }) => (
                <FormItem>
                  <div className="flex flex-wrap items-center gap-2.5">
                    <FormLabel className="block text-sm font-medium text-gray-700">
                      Primary Phone
                    </FormLabel>
                    <FormMessage />
                  </div>
                  <FormControl>
                    <PhoneInput
                      {...field}
                      countries={allowedCountries}
                      defaultCountry="US"
                      disabled={!isAdmin && !limitedAccess && !isHR}
                    />
                  </FormControl>
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="cellphone"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="block text-sm font-medium text-gray-700">
                    Cell Phone
                  </FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      className="mt-1 block w-full"
                      disabled={!isAdmin && !limitedAccess && !isHR}
                    />
                  </FormControl>
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="ext"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="block text-sm font-medium text-gray-700">Ext</FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      className="mt-1 block w-full"
                      disabled={!isAdmin && !limitedAccess && !isHR}
                    />
                  </FormControl>
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <div className="flex flex-wrap items-center gap-2.5">
                    <FormLabel className="block text-sm font-medium text-gray-700">Email</FormLabel>
                    <FormMessage />
                  </div>
                  <FormControl>
                    <Input
                      {...field}
                      className="mt-1 block w-full"
                      disabled={!isAdmin && !limitedAccess && !isHR}
                    />
                  </FormControl>
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="phoneEmail"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="block text-sm font-medium text-gray-700">
                    Email-to-SMS
                  </FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      className="mt-1 block w-full"
                      disabled={!isAdmin && !limitedAccess && !isHR}
                    />
                  </FormControl>
                </FormItem>
              )}
            />

            <div className="grid grid-cols-2 items-center gap-6 sm:col-span-2 md:col-span-3">
              <FormField
                control={form.control}
                name="pantSize"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="block text-sm font-medium text-gray-700">
                      Pant size
                    </FormLabel>
                    <FormControl>
                      <Input {...field} className="mt-1 block w-full" />
                    </FormControl>
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="shirtSize"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="block text-sm font-medium text-gray-700">
                      Shirt size
                    </FormLabel>
                    <FormControl>
                      <SimpleSelect
                        values={SHIRT_SIZES}
                        placeholder="Select Shirt Size"
                        selectedValue={field.value}
                        {...field}
                      />
                    </FormControl>
                  </FormItem>
                )}
              />
            </div>
          </div>
        </div>

        <div>
          <h3 className="text-2xl font-semibold tracking-wide">Kalos Information</h3>
          <div className="grid grid-cols-1 gap-6 rounded-lg border bg-gray-50 p-6 shadow sm:grid-cols-2 md:grid-cols-3">
            <FormField
              control={form.control}
              name="empTitle"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="block text-sm font-medium text-gray-700">Title</FormLabel>
                  <FormControl>
                    {/*
                      The chart's admins pick from the managed list; everyone
                      else keeps the free-text field.

                      Picking writes both roleId and empTitle: the id is what
                      the directory and the chart read, the text keeps every
                      other empTitle consumer working.
                    */}
                    {hasOrgChartPermission ? (
                      <JobTitlePicker
                        roleId={form.watch('roleId')}
                        fallbackTitle={field.value}
                        departmentId={form.watch('employeeDepartmentID')}
                        onChange={({ roleId, name }) => {
                          form.setValue('roleId', roleId, { shouldDirty: true });
                          form.setValue('empTitle', name, { shouldDirty: true });
                        }}
                      />
                    ) : (
                      <Input
                        {...field}
                        className="mt-1 block w-full"
                        disabled={!isAdmin && !limitedAccess}
                      />
                    )}
                  </FormControl>
                </FormItem>
              )}
            />
            {/*
              Teams are group_member rows rather than a userz column, so this
              writes on selection instead of on Save. It sits in the grid with
              Department and Supervisor because that is where HR sets everything
              else about a person.
            */}
            {hasOrgChartPermission ? (
              <FormItem>
                <FormLabel className="block text-sm font-medium text-gray-700">Team</FormLabel>
                <TeamMultiSelect value={teamIds} onChange={setTeamIds} />
              </FormItem>
            ) : null}
            <FormField
              control={form.control}
              name="employeeDepartmentID"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="block text-sm font-medium text-gray-700">
                    Department
                  </FormLabel>
                  <SimpleSelect
                    placeholder="Select Employee Department"
                    onChange={(value) => field.onChange(Number(value))}
                    disabled={!isHR}
                    selectedValue={field.value.toString()}
                    values={
                      departmentsQuery.data?.results.map((d) => ({
                        label: `${d.value} - ${d.description}`,
                        value: d.id.toString(),
                      })) ?? []
                    }
                  />
                  <FormMessage />
                </FormItem>
              )}
            />
            {isHvacTechWatch === 1 && (
              <FormField
                control={form.control}
                name="employeeTradeId"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="block text-sm font-medium text-gray-700">Trade</FormLabel>
                    <SimpleSelect
                      placeholder="Select Trade"
                      onChange={(value) => field.onChange(Number(value))}
                      disabled={!isAdmin}
                      selectedValue={field.value ? field.value.toString() : ''}
                      values={tradeOptions}
                    />
                    <FormDescription className="text-xs text-gray-500">
                      Determines which dispatch board this technician appears on
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}
            <FormField
              control={form.control}
              name="employeeLanguage"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="block text-sm font-medium text-gray-700">
                    Language
                  </FormLabel>
                  <SimpleSelect
                    placeholder="Select Language"
                    onChange={(value) =>
                      field.onChange(value === EMPLOYEE_LANGUAGE_NONE ? '' : value)
                    }
                    disabled={!isHR}
                    selectedValue={field.value || EMPLOYEE_LANGUAGE_NONE}
                    values={EMPLOYEE_LANGUAGES}
                  />
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="managedBy"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="block text-sm font-medium text-gray-700">
                    Assigned Supervisor
                  </FormLabel>
                  <TechniciansComboboxPicker
                    placeholder="None Assigned"
                    disabled={!isAdmin && !limitedAccess && !isHR}
                    onSelect={(user) => field.onChange(user?.id ?? 0)}
                    selected={field.value.toString()}
                  />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="empBadgeId"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="block text-sm font-medium text-gray-700">
                    Workers Comp Code
                  </FormLabel>
                  <FormControl>
                    <SimpleSelect
                      placeholder="Select Workers Comp Code"
                      onChange={(value) => field.onChange(Number(value))}
                      disabled={!isAdmin && !limitedAccess && !isHR}
                      selectedValue={field.value.toString()}
                      values={[
                        { label: 'Select Workers Comp Code', value: '0' },
                        ...WORKERS_COMP_CODES.map((code) => ({
                          label: `WC-${code.label}`,
                          value: code.value.toString(),
                        })),
                      ]}
                    />
                  </FormControl>
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="isContractor"
              render={({ field }) => (
                <FormItem className="flex items-center space-x-2 space-y-0">
                  <FormLabel className="text-sm font-medium text-gray-700">Contractor</FormLabel>
                  <FormDescription className="text-xs text-gray-500">Yes/No</FormDescription>
                  <Checkbox
                    checked={field.value}
                    onCheckedChange={(checked) => field.onChange(checked === true)}
                    disabled={!isAdmin && !limitedAccess && !isHR}
                  />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="userSector"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="block text-sm font-medium text-gray-700">
                    Market Sector
                  </FormLabel>
                  <FormControl>
                    <SimpleSelect
                      placeholder="Select Market Sector"
                      onChange={(value) =>
                        form.setValue('userSector', Number(value), {
                          shouldValidate: true,
                          shouldDirty: true,
                          shouldTouch: true,
                        })
                      }
                      disabled={!isAdmin && !limitedAccess && !isHR}
                      selectedValue={field.value.toString()}
                      values={EMPLOYEE_SECTORS.map((option) => ({
                        label: option.label,
                        value: option.value.toString(),
                      }))}
                    />
                  </FormControl>
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="userJobType"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="block text-sm font-medium text-gray-700">
                    Job Types
                  </FormLabel>

                  <FormControl>
                    <MultipleSelect
                      options={jobTypesQuery.data || []}
                      selected={field.value ? field.value.split(',').filter(Boolean) : []}
                      onChange={(values) => field.onChange(values.join(','))}
                      placeholder="Select job types..."
                      loading={jobTypesQuery.isLoading}
                      disabled={!isAdmin && !limitedAccess && !isHR}
                    />
                  </FormControl>
                </FormItem>
              )}
            />
          </div>
        </div>

        <div>
          <h3 className="text-2xl font-semibold tracking-wide">Legacy Permissions</h3>
          <div className="grid grid-cols-1 gap-6 rounded-lg border bg-white p-6 shadow sm:grid-cols-2 md:grid-cols-3">
            <FormField
              control={form.control}
              name="isAdmin"
              render={({ field }) => (
                <FormItem className="flex items-center space-x-2 space-y-0">
                  <FormLabel className="text-sm font-medium text-gray-700">
                    Admin Menu Rights
                  </FormLabel>
                  <FormDescription className="text-xs text-gray-500">Yes/No</FormDescription>
                  <Checkbox
                    checked={field.value === 1}
                    onCheckedChange={(checked) => field.onChange(checked ? 1 : 0)}
                    disabled={!isAdmin}
                  />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="paidServiceCallStatus"
              render={({ field }) => (
                <FormItem className="flex items-center space-x-2 space-y-0">
                  <FormLabel className="text-sm font-medium text-gray-700">
                    Paid Job Status
                  </FormLabel>
                  <FormDescription className="text-xs text-gray-500">Yes/No</FormDescription>
                  <Checkbox
                    checked={field.value === 1}
                    onCheckedChange={(checked) => field.onChange(checked ? 1 : 0)}
                    disabled={!isAdmin}
                  />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="showBilling"
              render={({ field }) => (
                <FormItem className="flex items-center space-x-2 space-y-0">
                  <FormLabel className="text-sm font-medium text-gray-700">
                    Show billing to user
                  </FormLabel>
                  <FormDescription className="text-xs text-gray-500">Yes/No</FormDescription>
                  <Checkbox
                    checked={field.value === 1}
                    onCheckedChange={(checked) => field.onChange(checked ? 1 : 0)}
                    disabled={!isAdmin}
                  />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="isOfficeStaff"
              render={({ field }) => (
                <FormItem className="flex items-center space-x-2 space-y-0">
                  <FormLabel className="text-sm font-medium text-gray-700">Office Staff</FormLabel>
                  <FormDescription className="text-xs text-gray-500">Yes/No</FormDescription>
                  <Checkbox
                    checked={field.value === 1}
                    onCheckedChange={(checked) => field.onChange(checked ? 1 : 0)}
                    disabled={!isAdmin}
                  />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="isHvacTech"
              render={({ field }) => (
                <FormItem className="flex items-center space-x-2 space-y-0">
                  <FormLabel className="text-sm font-medium text-gray-700">
                    Display On Dispatch?
                  </FormLabel>
                  <FormDescription className="text-xs text-gray-500">Yes/No</FormDescription>
                  <Checkbox
                    checked={field.value === 1}
                    onCheckedChange={(checked) => field.onChange(checked ? 1 : 0)}
                    disabled={!isAdmin}
                  />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="techAssist"
              render={({ field }) => (
                <FormItem className="flex items-center space-x-2 space-y-0">
                  <FormLabel className="text-sm font-medium text-gray-700">Tech Assist</FormLabel>
                  <FormDescription className="text-xs text-gray-500">Yes/No</FormDescription>
                  <Checkbox
                    checked={field.value === 1}
                    onCheckedChange={(checked) => field.onChange(checked ? 1 : 0)}
                    disabled={!isAdmin}
                  />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="isColorMute"
              render={({ field }) => (
                <FormItem className="flex items-center space-x-2 space-y-0">
                  <FormLabel className="text-sm font-medium  text-gray-700">
                    Color Mute [2017]
                  </FormLabel>
                  <FormDescription className="text-xs text-gray-500">Yes/No</FormDescription>
                  <Checkbox
                    checked={field.value === 1}
                    onCheckedChange={(checked) => field.onChange(checked ? 1 : 0)}
                    disabled={loggedInUserId !== user.id}
                  />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="serviceCalls"
              render={({ field }) => (
                <FormItem className="flex items-center space-x-2 space-y-0">
                  <FormLabel className="text-sm font-medium text-gray-700">
                    Mileage Submission Access
                  </FormLabel>
                  <FormDescription className="text-xs text-gray-500">Yes/No</FormDescription>
                  <Checkbox
                    checked={field.value === 1}
                    onCheckedChange={(checked) => field.onChange(checked ? 1 : 0)}
                    disabled={!isAdmin}
                  />
                </FormItem>
              )}
            />
          </div>
        </div>

        <div className="mt-4 grid grid-cols-2 gap-2 overflow-hidden rounded-lg border bg-white p-4 shadow md:grid-cols-3 md:gap-3">
          <FormField
            control={form.control}
            name="hireDate"
            render={({ field }) => (
              <FormItem className="col-span-2 sm:col-span-1">
                <FormLabel>Hire Date</FormLabel>

                <DateInput
                  value={new Date(field.value)}
                  disabled={!isHR}
                  onChange={field.onChange}
                />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="overrideMonth"
            render={({ field }) => (
              <FormItem className="col-span-2 sm:col-span-1">
                <FormLabel>Override Hire Month</FormLabel>
                <SimpleSelect
                  placeholder="Select Month"
                  disabled={!isHR}
                  onChange={(value) => field.onChange(Number(value))}
                  selectedValue={field.value?.toString()}
                  values={monthsSelectOptions}
                />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="ptoValue"
            render={({ field }) => (
              <FormItem className="col-span-2 sm:col-span-1">
                <FormLabel>Currently Allocated PTO</FormLabel>
                <FormControl>
                  <Input
                    type="number"
                    {...field}
                    disabled={true}
                    value={associatedPto?.totalHours ?? 0}
                  />
                </FormControl>
              </FormItem>
            )}
          />
        </div>
        <div>
          <h3 className="text-2xl font-semibold tracking-wide">Employee Photo</h3>

          <div className="flex items-center space-x-6">
            <Avatar className="h-40 w-40">
              <AvatarImage
                className="object-cover"
                src={
                  imageWatch === ''
                    ? undefined
                    : employeeUploadedPhoto || (imageWatch && imageQuery.data) || undefined
                }
              />
              <AvatarFallback>{`${user.firstname.at(0)?.toUpperCase() || ''} ${
                user.lastname.at(0)?.toUpperCase() || ''
              }`}</AvatarFallback>
            </Avatar>
            <div>
              <FormField
                control={form.control}
                name="image"
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <Input
                        disabled={disableProfileImageField}
                        type="file"
                        accept="image/*"
                        onChange={(e) => {
                          const file = e.target.files?.[0];
                          if (file) {
                            handleEmployeePhotoUpload(file);
                          }
                        }}
                      />
                    </FormControl>
                  </FormItem>
                )}
              />

              {(employeeUploadedPhoto || (imageQuery.data && !disableProfileImageField)) && (
                <Button variant="outline" className="mt-2" onClick={handleDeleteEmployeePhoto}>
                  Delete Photo
                </Button>
              )}
            </div>
          </div>
        </div>
      </form>
    </Form>
  );
};
